import { pdf } from '@react-pdf/renderer';
import JSZip from 'jszip';

import {
  CompletedProgramAssessmentsDataQuery,
  ProgramAssessmentDataFragment,
  ProgramNoteDataFragment,
} from '../../generated/graphql';

import ProgramAssessmentPDF from '../components/ProgramActivitySlideover/ProgramAssessmentPDF';
import ProgramNotePDF, {
  ProgramNotePdfFields,
} from '../components/ProgramNotePDF';

import { getTimeStamp } from './time';

const sanitizePdfFilename = (name: string): string =>
  name.replace(/[/\\?%*:|"<>]/g, '-').trim() || 'Form';

export const handleLibraryFormPdfDownload = async (
  blob: Blob | null,
  formName: string,
  onSuccess?: () => void,
) => {
  if (blob) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizePdfFilename(formName)} - Printable.pdf`;
    link.click();
    URL.revokeObjectURL(url);
    onSuccess?.();
  }
};

export const handlePdfDownload = async (
  blob: Blob | null,
  programAssessment: ProgramAssessmentDataFragment,
  patientName: string,
  onSuccess?: () => void,
) => {
  if (blob) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const dateCompleted = getTimeStamp(programAssessment?.completedAt, false);
    const title = programAssessment?.assessment?.name;

    const clientName = patientName || 'Client';
    link.download = `${clientName} - ${title} - ${dateCompleted}.pdf`;

    link.click();
    URL.revokeObjectURL(url);
    onSuccess?.();
  }
};

export const downloadAllPdfs = async (
  completedAssessments: CompletedProgramAssessmentsDataQuery['completedProgramAssessmentsByAssessment'],
  patientFirstName?: string,
  isProviderUser = true,
) => {
  try {
    if (!completedAssessments?.length) {
      throw new Error('No assessments to download');
    }

    // Generate all PDFs in parallel using pdf.createPDF instead of BlobProvider
    const pdfPromises = completedAssessments.map(async (assessment) => {
      try {
        const pdfDocument = (
          <ProgramAssessmentPDF
            programAssessment={assessment}
            isProviderUser={isProviderUser}
          />
        );

        const blob = await pdf(pdfDocument).toBlob();
        return { blob, assessment };
      } catch (error) {
        console.error(
          'PDF generation error for assessment',
          assessment.id,
          ':',
          error,
        );
        return { blob: null, assessment };
      }
    });

    // Wait for all PDFs to be generated
    const pdfs = await Promise.all(pdfPromises);
    console.log('PDFs generated:', pdfs.length);

    // Download all valid PDFs
    let downloadCount = 0;
    for (const { blob, assessment } of pdfs) {
      if (blob) {
        console.log('Downloading PDF for assessment:', assessment.id);
        await handlePdfDownload(blob, assessment, patientFirstName);
        downloadCount++;
      } else {
        console.warn('No blob generated for assessment:', assessment.id);
      }
    }

    if (downloadCount === 0) {
      throw new Error('No PDFs were generated successfully');
    }

    return downloadCount;
  } catch (error) {
    console.error('PDF download error:', error);
    throw error;
  }
};

export const handleProgramNotePdfDownload = async (
  blob: Blob | null,
  programNote: ProgramNotePdfFields,
  patientName: string,
  onSuccess?: () => void,
) => {
  if (blob) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const dateCreated = programNote?.createdAt
      ? getTimeStamp(programNote.createdAt, false)
      : '';
    const title = programNote?.title || 'Untitled note';

    const clientName = patientName || 'Client';
    link.download = `${clientName} - Private Note - ${title}${
      dateCreated ? ` - ${dateCreated}` : ''
    }.pdf`;

    link.click();
    URL.revokeObjectURL(url);
    onSuccess?.();
  }
};

export type ProgramNotePdfOptions = {
  patientName?: string;
  wordmarkLogoUrl?: string | null;
  participantId?: string | null;
};

export const downloadAllProgramNotes = async (
  programNotes: ProgramNoteDataFragment[],
  options?: ProgramNotePdfOptions,
) => {
  const patientName = options?.patientName;
  const wordmarkLogoUrl = options?.wordmarkLogoUrl ?? null;
  const participantId = options?.participantId ?? null;
  try {
    if (!programNotes?.length) {
      throw new Error('No private notes to download');
    }

    const zip = new JSZip();

    // Generate PDFs sequentially to ensure all are created properly
    const pdfs = [];
    for (let index = 0; index < programNotes.length; index++) {
      const programNote = programNotes[index];
      try {
        const pdfDocument = (
          <ProgramNotePDF
            programNote={programNote}
            patientName={patientName}
            wordmarkLogoUrl={wordmarkLogoUrl}
            participantId={participantId}
          />
        );

        const blob = await pdf(pdfDocument).toBlob();
        pdfs.push({ blob, programNote });
      } catch (error) {
        console.error(
          'PDF generation error for program note',
          programNote.id,
          ':',
          error,
        );
        pdfs.push({ blob: null, programNote });
      }
    }

    // Add all valid PDFs to the zip inside a folder
    let addedCount = 0;
    let fileIndex = 1;
    const clientName = patientName || 'Client';
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const folderName = `${clientName} - Private Notes - ${timestamp}`;

    for (const { blob, programNote } of pdfs) {
      if (blob) {
        const dateCreated = getTimeStamp(programNote?.createdAt, false);
        const title =
          programNote?.title?.replace(/[/\\?%*:|"<>]/g, '-') || 'Note'; // Sanitize filename
        const fileName = `${fileIndex}. ${title} - ${dateCreated}.pdf`;

        // Add file inside folder
        zip.file(`${folderName}/${fileName}`, blob);
        addedCount++;
        fileIndex++;
      }
    }

    if (addedCount === 0) {
      throw new Error('No PDFs were generated successfully');
    }

    // Generate the zip file
    const zipBlob = await zip.generateAsync({ type: 'blob' });

    // Download the zip file
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;

    link.download = `${clientName} - Private Notes - ${timestamp}.zip`;

    link.click();
    URL.revokeObjectURL(url);

    return addedCount;
  } catch (error) {
    console.error('Private notes PDF download error:', error);
    throw error;
  }
};
